import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import Logo from "../ui/Logo";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import AuthGraphShowcase from "./AuthGraphShowcase";
import OAuthButtons from "./OAuthButtons";
import { authFloatContainer, authFloatItem } from "./authMotion";

export default function RegisterPage({ onSwitch, onSuccess }) {
  const { register } = useAuth();
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPw) { setError("Passwords do not match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(email, password, nickname.trim() || null);
      if (onSuccess) onSuccess(); // Call success callback
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg)" }} data-testid="register-page">
      <div className="relative flex items-center justify-center px-6 py-10">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle at 25% 15%, rgba(163,230,53,0.12), transparent 45%), radial-gradient(circle at 75% 85%, rgba(52,211,153,0.08), transparent 50%)",
          }}
        />

        <div className="w-full max-w-[520px]">
          <motion.div
            className="rounded-[30px] border px-8 py-9 sm:px-10 sm:py-10"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.95) 100%)",
              borderColor: "rgba(255,255,255,0.1)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            }}
            variants={authFloatContainer}
            initial={reduceMotion ? false : "hidden"}
            animate={reduceMotion ? undefined : "show"}
          >
            <motion.div className="text-center mb-8" variants={authFloatItem}>
              <div className="flex items-center justify-center mb-5">
                <Logo size="md" />
              </div>
              <h1 className="text-5xl font-bold leading-tight mb-2" style={{ color: "#F3F4FF", letterSpacing: "-0.02em" }}>Create your free account</h1>
              <p className="text-base" style={{ color: "#9EA3C6" }}>
                Start optimizing AI visibility with Scopuz.
              </p>
            </motion.div>

            <motion.div variants={authFloatItem} className="mb-4">
              <OAuthButtons onSuccess={() => onSuccess && onSuccess()} onError={(msg) => setError(msg)} />
            </motion.div>

            <motion.div className="flex items-center gap-4 my-4" variants={authFloatItem}>
              <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.1)" }} />
              <span className="text-xs font-medium uppercase tracking-widest" style={{ color: "#6B7280" }}>or</span>
              <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.1)" }} />
            </motion.div>

            <motion.form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              {error && (
                <div className="rounded-xl bg-red-400/10 border border-red-400/20 px-4 py-3 text-sm text-red-400" data-testid="register-error">{error}</div>
              )}

              <motion.input
                data-testid="register-nickname-input"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full h-12 rounded-2xl px-4 text-base"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.2)", color: "#F3F4FF" }}
                placeholder="Nickname (optional)"
                maxLength={50}
                variants={authFloatItem}
              />

              <motion.input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 rounded-2xl px-4 text-base"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.2)", color: "#F3F4FF" }}
                placeholder="Enter email address"
                required
                variants={authFloatItem}
              />

              <motion.div className="relative" variants={authFloatItem}>
                <input
                  data-testid="register-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 rounded-2xl px-4 pr-12 text-base"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.2)", color: "#F3F4FF" }}
                  placeholder="Create password"
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors" data-testid="register-toggle-password">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </motion.div>

              <motion.input
                data-testid="register-confirm-password-input"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full h-12 rounded-2xl px-4 text-base"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.2)", color: "#F3F4FF" }}
                placeholder="Confirm password"
                required
                variants={authFloatItem}
              />

              <motion.button
                data-testid="register-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-primary text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={reduceMotion ? {} : { scale: 1.01 }}
                whileTap={reduceMotion ? {} : { scale: 0.99 }}
                variants={authFloatItem}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Account <ArrowRight className="w-4 h-4" /></>}
              </motion.button>
            </motion.form>

            <motion.p className="text-xs text-center mt-6" style={{ color: "#8A90B0" }} variants={authFloatItem}>
              By continuing, you agree to Scopuz's Terms and Data Policy.
            </motion.p>

            <motion.p className="text-sm text-center mt-8" style={{ color: "#9EA3C6" }} variants={authFloatItem}>
              Already have an account?{" "}
              <button data-testid="register-switch-to-login" onClick={onSwitch} className="font-medium transition-opacity hover:opacity-70" style={{ color: "var(--primary)" }}>Sign in</button>
            </motion.p>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="hidden lg:block"
        variants={authFloatItem}
        initial={reduceMotion ? false : "hidden"}
        animate={reduceMotion ? undefined : "show"}
      >
        <AuthGraphShowcase />
      </motion.div>
    </div>
  );
}
